import { useEffect, useMemo, useRef, useState } from 'react';
import {
  answered,
  emptyDraft,
  missingPhotos as photosStillNeeded,
  newId,
  outOfRange as numbersOutOfRange,
  type Draft,
} from '@relay-towing/ui';
import type {
  ChecklistItem,
  InspectionPhotoSource,
  InspectionSubjectType,
  PhotoMeta,
  SubmitAnswer,
  SubmitPhoto,
} from '../api/truckInspections';
import {
  useDriverChecklists,
  useInspectionTrucks,
  useStartInspection,
  useSubmitInspection,
  useUploadInspectionPhoto,
} from './useTruckInspections';
import {
  connect as connectOutbox,
  enqueue,
  listForRun,
  remove as removeFromOutbox,
  removeForRun,
} from '../lib/photoOutbox';
import { clearRun, loadRun, saveRun, type SavedRun } from '../lib/runStorage';

export type RunStage = 'subject' | 'checklist' | 'sign';

export type RunSubject = SavedRun['subject'];

/** A photo that is on the phone but not yet on the server. */
export interface PendingPhoto {
  localId: string;
  blob: Blob;
}

/** What was filed, frozen at send time so the done screen cannot drift. */
export interface FiledRun {
  id: string;
  passed: number;
  flagged: number;
}

/** What stands between the member and sending, and the question it is on. */
export interface RunBlock {
  message: string;
  itemId?: string;
}

/** A fix taken on the run screen, to say where the check was done. */
export interface RunLocation {
  latitude: number;
  longitude: number;
}

const SAVE_DELAY_MS = 1000;

/**
 * The outbox is keyed by the question a photo answers. A photo of the thing
 * being checked answers no question, so it rides under this id and is routed
 * out again on the way back in.
 */
export const REPORT_PHOTO_ITEM = '__report__';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const messageFrom = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? fallback;

/**
 * The signature comes off the canvas as a base64 data URL, which is tens of
 * kilobytes. It is stored as a URL, so it has to become a real file first.
 */
const signatureToFile = async (dataUrl: string): Promise<File> => {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' });
};

interface RunState {
  drafts: Record<string, Draft>;
  notes: string;
  signedName: string;
  pending: Record<string, PendingPhoto[]>;
  reportPhotoUrls: string[];
  photoMeta: Record<string, PhotoMeta>;
  subject: RunSubject | null;
}

const hasAnything = (state: RunState) =>
  Object.values(state.drafts).some(
    (draft) =>
      draft.status ||
      draft.valueNumber?.trim() ||
      draft.valueText?.trim() ||
      draft.valueList.length ||
      draft.note?.trim() ||
      draft.photoUrls.length
  ) ||
  Object.values(state.pending).some((list) => list.length > 0) ||
  state.reportPhotoUrls.length > 0 ||
  Boolean(state.notes.trim());

/**
 * One checklist, run on one truck or one piece of equipment: everything but
 * how it is drawn. Both shells' run screens read it, so the old screen and
 * the new one start, save, restore, queue photos and file a report the same
 * way until the old shell goes.
 *
 * A run is kept on the phone (runStorage) a second after each change and at
 * once on the way out, under the clientSubmissionId it started with, so a
 * killed app reopens on the same run and a retried submit files once.
 * Photos go into the outbox (photoOutbox) rather than straight to the
 * server, so a yard with patchy signal loses none of them.
 *
 * `notify` is how it tells the member something went wrong: a photo the
 * server refused, a send that did not go. Each screen says it its own way.
 */
export function useInspectionRun({
  templateId,
  signerName,
  notify,
}: {
  templateId: string;
  /** The member's name, put in the signature's name box once it arrives. */
  signerName: string;
  notify: (message: string) => void;
}) {
  const start = useStartInspection();
  const submit = useSubmitInspection();
  const upload = useUploadInspectionPhoto();
  // What the checklist covers has to be known before start can be called, and
  // the list the home screen already fetched is where it is.
  const checklists = useDriverChecklists();

  const [stage, setStage] = useState<RunStage>('subject');
  // What is being checked. Null until the driver says, which is what keeps
  // the subject stage on screen.
  const [subject, setSubject] = useState<RunSubject | null>(null);
  // Which pill the driver tapped, and nothing more. It is only read on a
  // checklist that covers either; the other two are not asked.
  const [subjectKind, setSubjectKind] = useState<InspectionSubjectType | null>(
    null
  );
  const [equipmentLabel, setEquipmentLabel] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [signedName, setSignedName] = useState('');
  const [notes, setNotes] = useState('');
  // Photos of the subject itself. They go on the report, not on any answer.
  const [reportPhotoUrls, setReportPhotoUrls] = useState<string[]>([]);
  // What the office is told about each photo, by URL: whether the driver
  // opened the camera or the roll, and whatever the image's own EXIF said.
  const [photoMeta, setPhotoMeta] = useState<Record<string, PhotoMeta>>({});
  const [sending, setSending] = useState(false);
  // Set once the server has the report. Turns the screen into the receipt.
  const [filed, setFiled] = useState<FiledRun | null>(null);
  // Photos on the phone that have not reached the server, per question.
  const [pending, setPending] = useState<Record<string, PendingPhoto[]>>({});
  const [online, setOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine
  );
  // Made once, when the checklist opens, so both taps of a retried submit send
  // the same one and the server can tell them apart from a second walk-around.
  // A run restored from the phone keeps the id it was started with. Lazily,
  // because a useRef argument is built on every render and thrown away.
  const submissionId = useRef<string>('');
  if (!submissionId.current) submissionId.current = newId();
  // Ids the outbox has already turned into URLs, so a late list of pending
  // photos cannot put one back.
  const uploadedIds = useRef(new Set<string>());
  const restored = useRef(false);
  const cleared = useRef(false);
  const uploadRef = useRef(upload.mutateAsync);
  uploadRef.current = upload.mutateAsync;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const latest = useRef<RunState>({
    drafts,
    notes,
    signedName,
    pending,
    reportPhotoUrls,
    photoMeta,
    subject,
  });
  latest.current = {
    drafts,
    notes,
    signedName,
    pending,
    reportPhotoUrls,
    photoMeta,
    subject,
  };

  const data = start.data;
  const template = data?.template;
  const items = useMemo(() => template?.items ?? [], [template]);
  // The row this run was started from. It carries what the checklist covers
  // and the subject it was last run on.
  const row = useMemo(
    () => checklists.data?.find((one) => one.id === templateId),
    [checklists.data, templateId]
  );
  const covers = row?.covers;
  /*
   * What the driver is being asked for. The checklist decides unless it covers
   * either, and only then does what the driver picked count.
   *
   * The pick used to win outright. So a checklist the office switched to
   * equipment still showed the truck list to a driver who had picked a truck
   * on it while it covered either, and the server then refused the run.
   */
  const kind: InspectionSubjectType =
    covers === 'ANY' ? (subjectKind ?? 'TRUCK') : (covers ?? 'TRUCK');
  const trucks = useInspectionTrucks(
    stage === 'subject' && Boolean(covers) && kind === 'TRUCK'
  );
  const startMutate = start.mutate;

  /**
   * Open the checklist on this subject.
   *
   * The only way into the form. Both the driver picking on the subject stage
   * and a run restored from the phone come through here, so a start that
   * fails lands in the same place: back on the subject stage with the
   * server's own sentence said.
   */
  const startOn = (chosen: RunSubject) => {
    setSubject(chosen);
    startMutate(
      {
        templateId,
        subject: chosen.truckId
          ? { truckId: chosen.truckId }
          : { equipmentLabel: chosen.equipmentLabel },
      },
      {
        onSuccess: () => setStage('checklist'),
        onError: (error: unknown) => {
          notifyRef.current(messageFrom(error, 'Could not open the checklist'));
          setSubject(null);
          setStage('subject');
        },
      }
    );
  };

  /*
   * A checklist that is not on this driver's list cannot be run, and a list
   * that never arrived is not the same thing as an empty one. Not over the
   * receipt: the list is refetched the moment a report is filed, and a
   * refetch that fails must not take the done screen away. And only when
   * there is nothing to run on: the list is asked again every time the
   * screen opens, and a driver in a dead spot must not be thrown out of a
   * walk-around because that background ask timed out.
   */
  const unavailable: 'error' | 'missing' | null =
    filed || checklists.isPending
      ? null
      : checklists.isError && !checklists.data
        ? 'error'
        : !row
          ? 'missing'
          : null;

  const labelPrefilled = useRef(false);

  // The subject stage belongs to the checklist in the path. The router keeps
  // the screen mounted when only that id changes, so without this a pill
  // tapped on the last checklist, and the name typed under it, were still on
  // screen for the next one.
  //
  // Above the prefill below, so that a checklist opening on its last subject
  // fills the box after this has emptied it rather than before.
  useEffect(() => {
    setSubjectKind(null);
    setEquipmentLabel('');
    labelPrefilled.current = false;
  }, [templateId]);

  // What this checklist was last run on is the likeliest answer to what it is
  // being run on now. Once, so the box can be cleared and retyped.
  //
  // Only a checklist written for equipment. One that covers either was as
  // likely last run on a truck, and the list row does not say which, so it
  // would put "Truck 12" in the box for a driver holding a dolly.
  useEffect(() => {
    const last = row?.lastRun?.subjectLabel;
    if (labelPrefilled.current || !last || row?.covers !== 'EQUIPMENT') return;
    labelPrefilled.current = true;
    setEquipmentLabel((current) => current || last);
  }, [row]);

  // Prefill once, when the driver's name arrives. Watching signedName meant
  // clearing the field put the name straight back, so it could not be retyped.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !signerName) return;
    prefilled.current = true;
    setSignedName((current) => current || signerName);
  }, [signerName]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Pick the run back up where the phone left it, then wire the outbox to
  // this run so photos taken before the app was killed still go up.
  useEffect(() => {
    if (!templateId) return;
    restored.current = false;
    cleared.current = false;
    // A new checklist is a new run, so the receipt from the last one goes. The
    // router keeps the screen mounted when only the id in the path changes,
    // and without this the done screen sat over the fresh checklist.
    setFiled(null);
    const { run, stale } = loadRun(templateId);
    stale.forEach((id) => void removeForRun(id));
    // Every piece of the form is written here, not only the pieces a saved run
    // has. A checklist with nothing saved used to keep the last one's answers
    // in state and file them against this run.
    setDrafts(
      run
        ? Object.fromEntries(
            Object.entries(run.drafts).map(([id, draft]) => [
              id,
              { ...emptyDraft(), ...draft, uploading: 0 },
            ])
          )
        : {}
    );
    setPending({});
    setNotes(run?.notes ?? '');
    setSignedName(run?.signedName || signerName);
    setReportPhotoUrls(run?.reportPhotoUrls ?? []);
    setPhotoMeta(run?.photoMeta ?? {});
    // The signature is never written to the phone, so it has nothing to be
    // restored from and everything to carry over. The sign stage belongs to
    // the walk-around that reached it, and the stage is set below.
    setSignature(null);
    // A saved run knows what it was being done on, so it reopens straight on
    // the form. Anything else asks first.
    if (run) {
      startOn(run.subject);
    } else {
      setSubject(null);
      setStage('subject');
    }
    submissionId.current = run ? run.submissionId : newId();
    // A blank form takes the prefill again. If the driver's name has not
    // arrived yet, dropping the ref lets the prefill effect put it in.
    prefilled.current = Boolean(run?.signedName || signerName);
    restored.current = true;

    const runId = submissionId.current;
    let disconnect: (() => void) | null = null;
    let gone = false;
    void listForRun(runId).then((queued) => {
      if (gone) return;
      const grouped: Record<string, PendingPhoto[]> = {};
      queued.forEach((photo) => {
        if (uploadedIds.current.has(photo.localId)) return;
        (grouped[photo.itemId] ??= []).push({
          localId: photo.localId,
          blob: photo.blob,
        });
      });
      setPending(grouped);
      disconnect = connectOutbox({
        runId,
        upload: (file) => uploadRef.current(file),
        onUploaded: ({ itemId, localId, url, source, ...exif }) => {
          // The same photo reported twice must not land twice.
          if (uploadedIds.current.has(localId)) return;
          uploadedIds.current.add(localId);
          // What the driver opened and what the image's own EXIF said, kept
          // beside the URL so it survives as far as submit.
          setPhotoMeta((current) => ({
            ...current,
            [url]: { source, ...exif },
          }));
          setPending((current) => ({
            ...current,
            [itemId]: (current[itemId] ?? []).filter(
              (photo) => photo.localId !== localId
            ),
          }));
          if (itemId === REPORT_PHOTO_ITEM) {
            setReportPhotoUrls((current) => [...current, url]);
            return;
          }
          setDrafts((current) => {
            const draft = current[itemId] ?? emptyDraft();
            return {
              ...current,
              [itemId]: { ...draft, photoUrls: [...draft.photoUrls, url] },
            };
          });
        },
        onRefused: (photo) => {
          setPending((current) => ({
            ...current,
            [photo.itemId]: (current[photo.itemId] ?? []).filter(
              (one) => one.localId !== photo.localId
            ),
          }));
          notifyRef.current('The server refused a photo. Take it again.');
        },
      });
    });

    return () => {
      gone = true;
      disconnect?.();
    };
    // The checklist is the run. Nothing else here should restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Every change lands on the phone a second later. Not the signature: it is
  // drawn again at send time and it is too big for this.
  useEffect(() => {
    if (!templateId || !subject || !restored.current || cleared.current) return;
    const timer = setTimeout(() => {
      if (cleared.current) return;
      if (!hasAnything(latest.current)) {
        clearRun(templateId, submissionId.current);
        return;
      }
      saveRun({
        submissionId: submissionId.current,
        templateId,
        subject,
        savedAt: Date.now(),
        drafts,
        notes,
        signedName,
        reportPhotoUrls,
        photoMeta,
      });
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    drafts,
    notes,
    signedName,
    pending,
    reportPhotoUrls,
    photoMeta,
    templateId,
    subject,
  ]);

  // Leaving with nothing answered leaves nothing behind. Leaving with answers
  // saves them at once rather than waiting out the debounce.
  useEffect(() => {
    if (!templateId) return;
    return () => {
      if (cleared.current) return;
      const runId = submissionId.current;
      // The subject is read through the ref: this runs long after the render
      // that named it, and a stale closure would save the wrong one.
      const saved = latest.current.subject;
      if (!saved || !hasAnything(latest.current)) {
        clearRun(templateId, runId);
        void removeForRun(runId);
        return;
      }
      saveRun({
        submissionId: runId,
        templateId,
        subject: saved,
        savedAt: Date.now(),
        drafts: latest.current.drafts,
        notes: latest.current.notes,
        signedName: latest.current.signedName,
        reportPhotoUrls: latest.current.reportPhotoUrls,
        photoMeta: latest.current.photoMeta,
      });
    };
  }, [templateId]);

  const draftFor = (id: string) => drafts[id] ?? emptyDraft();

  // What the rules see: a pending photo counts as a photo on the screen,
  // since it is on the phone and will go up. The shared rules only read
  // photoUrls, so each pending one stands in as a placeholder.
  const viewDrafts = useMemo(() => {
    const view: Record<string, Draft> = { ...drafts };
    Object.entries(pending).forEach(([itemId, list]) => {
      if (!list.length) return;
      const draft = view[itemId] ?? emptyDraft();
      view[itemId] = {
        ...draft,
        photoUrls: [
          ...draft.photoUrls,
          ...list.map((photo) => `pending:${photo.localId}`),
        ],
      };
    });
    return view;
  }, [drafts, pending]);
  const viewFor = (id: string) => viewDrafts[id] ?? emptyDraft();
  const pendingFor = (id: string) => pending[id] ?? [];
  // Report photos sit in the same pending map under REPORT_PHOTO_ITEM, so a
  // send waits for them the same way it waits for a question's photo.
  const pendingCount = Object.values(pending).reduce(
    (sum, list) => sum + list.length,
    0
  );
  const reportPhotoCount =
    reportPhotoUrls.length + pendingFor(REPORT_PHOTO_ITEM).length;

  const setDraft = (id: string, patch: Partial<Draft>) =>
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] ?? emptyDraft()), ...patch },
    }));

  // The rules live in the library so the office run screen judges a checklist
  // exactly the way the phone does.
  const isAnswered = (item: ChecklistItem) => answered(item, viewFor(item.id));

  const required = items.filter((item) => item.isRequired);
  const doneCount = required.filter(isAnswered).length;
  const remaining = required.length - doneCount;
  const progress = required.length
    ? Math.round((doneCount / required.length) * 100)
    : 100;

  const failedItems = items.filter(
    (item) => draftFor(item.id).status === 'FAIL'
  );

  const missingPhotos = photosStillNeeded(items, viewDrafts);
  const outOfRange = numbersOutOfRange(items, drafts);

  // One line above the send button. Pending photos come first, since they are
  // what holds the send; being offline matters only when nothing is waiting.
  const statusLine =
    pendingCount > 0
      ? `${plural(pendingCount, 'photo')} pending upload`
      : !online
        ? 'Offline. Photos will send when you are back on'
        : null;
  const notSigned = Boolean(
    template?.requireSignature && (!signature || !signedName.trim())
  );

  /**
   * Photo URLs as the server wants them filed.
   *
   * `source` records which picker the driver opened, not where the file really
   * came from: `capture` is a hint that desktop browsers ignore and some phones
   * let you override. Nothing here checks it, and a photo with no entry, from
   * an older build or a run restored from one, is filed as an upload.
   */
  const photosFor = (urls: string[]): SubmitPhoto[] =>
    urls.map((url) => ({
      url,
      source: photoMeta[url]?.source ?? 'UPLOAD',
      takenAt: photoMeta[url]?.takenAt,
      latitude: photoMeta[url]?.latitude,
      longitude: photoMeta[url]?.longitude,
    }));

  /** A URL that is off the strip is off the report, so its story goes too. */
  const forgetPhoto = (url: string) =>
    setPhotoMeta((current) => {
      if (!(url in current)) return current;
      const next = { ...current };
      delete next[url];
      return next;
    });

  /** Takes an uploaded photo off a question, or off the report. */
  const removePhoto = (itemId: string, url: string) => {
    if (itemId === REPORT_PHOTO_ITEM) {
      setReportPhotoUrls((current) => current.filter((u) => u !== url));
    } else {
      setDrafts((current) => {
        const draft = current[itemId] ?? emptyDraft();
        return {
          ...current,
          [itemId]: {
            ...draft,
            photoUrls: draft.photoUrls.filter((u) => u !== url),
          },
        };
      });
    }
    forgetPhoto(url);
  };

  const addFiles = async (
    itemId: string,
    files: FileList | File[] | null,
    source: InspectionPhotoSource
  ) => {
    if (!files?.length) return;
    const list = Array.from(files).slice(0, 6);

    // Into the outbox, not straight to the server. The outbox sends it now if
    // it can and keeps it on the phone if it cannot.
    for (const file of list) {
      const localId = await enqueue(submissionId.current, itemId, file, source);
      if (uploadedIds.current.has(localId)) continue;
      setPending((current) => ({
        ...current,
        [itemId]: [...(current[itemId] ?? []), { localId, blob: file }],
      }));
    }
  };

  const removePending = (itemId: string, localId: string) => {
    setPending((current) => ({
      ...current,
      [itemId]: (current[itemId] ?? []).filter(
        (photo) => photo.localId !== localId
      ),
    }));
    void removeFromOutbox(localId);
  };

  /**
   * Back to naming the subject, with everything answered so far thrown away.
   *
   * The run keeps its submission id. Nothing has been sent under it, so it
   * is not a key the server has seen, and the outbox is connected to it for
   * as long as the screen is open. Minting a new one here would leave the
   * next photo enqueued under an id nothing is draining.
   */
  const changeSubject = () => {
    const runId = submissionId.current;
    clearRun(templateId, runId);
    void removeForRun(runId);
    setSubject(null);
    setDrafts({});
    setPending({});
    setNotes('');
    setSignature(null);
    setReportPhotoUrls([]);
    setPhotoMeta({});
    setStage('subject');
  };

  /** Whether changing the subject would throw anything away. */
  const hasWork = () => hasAnything(latest.current);

  /**
   * What still stands between the driver and sending, said out loud.
   *
   * Both ways out of the checklist ask this: the templates that ask for a
   * signature go on to the sign stage, the ones that do not go straight to the
   * confirm, and neither should get past a question with no photo on it.
   */
  const firstBlock = (): RunBlock | null => {
    if (remaining > 0) {
      return {
        message: `${remaining} question${remaining === 1 ? '' : 's'} still to answer`,
      };
    }
    if (missingPhotos.length > 0) {
      const [item] = missingPhotos;
      return {
        message:
          draftFor(item.id).status === 'FAIL'
            ? `Photograph what is wrong with ${item.label}`
            : `Add a photo for ${item.label}`,
        itemId: item.id,
      };
    }
    if (outOfRange.length > 0) {
      return {
        message: `Check the number for ${outOfRange[0].label}`,
        itemId: outOfRange[0].id,
      };
    }
    return null;
  };

  /**
   * Files the report. True once the server has it.
   *
   * `location` is only a fix taken on the run screen. The driver's stored
   * position is wherever they last pinged from, which can be hours old and
   * miles away, and writing that down as where the check was done puts a
   * wrong place in the record instead of no place.
   */
  const finish = async (location?: RunLocation | null): Promise<boolean> => {
    if (!template || !subject) return false;
    // The signature is uploaded before the report is posted, and during that
    // upload submit.isPending was still false, so the button stayed live and a
    // second tap sent the whole thing twice. One flag covers both halves.
    if (sending) return false;
    if (template.requireSignature && (!signature || !signedName.trim())) {
      notify('Sign the report before you send it');
      return false;
    }
    if (pendingCount > 0) {
      notify('Photos are still waiting to upload');
      return false;
    }
    // Not queued for later. The answers are already on the phone; the driver
    // sends when the signal is back.
    if (!navigator.onLine) {
      notify(
        'You are offline. Your answers are saved on this phone. Send it when you are back on.'
      );
      return false;
    }

    setSending(true);
    let signatureUrl: string | undefined;
    if (signature) {
      try {
        signatureUrl = (
          await upload.mutateAsync(await signatureToFile(signature))
        ).url;
      } catch {
        setSending(false);
        notify('Could not save the signature. Try again.');
        return false;
      }
    }

    const answers: SubmitAnswer[] = items
      .filter((item) => isAnswered(item) || item.isRequired)
      .map((item) => {
        const draft = draftFor(item.id);
        return {
          itemId: item.id,
          itemLabel: item.label,
          itemSection: item.section ?? undefined,
          itemType: item.answerType,
          status: item.answerType === 'PASS_FAIL' ? draft.status : undefined,
          valueNumber:
            item.answerType === 'NUMBER' && draft.valueNumber?.trim()
              ? Number(draft.valueNumber)
              : undefined,
          valueText:
            item.answerType === 'TEXT' ||
            item.answerType === 'SELECT' ||
            item.answerType === 'YES_NO'
              ? draft.valueText?.trim() || undefined
              : undefined,
          valueList:
            item.answerType === 'CHECKBOX' ? draft.valueList : undefined,
          note: draft.note?.trim() || undefined,
          photos: photosFor(draft.photoUrls),
        };
      });

    try {
      const report = await submit.mutateAsync({
        body: {
          templateId: template.id,
          ...(subject.truckId
            ? { truckId: subject.truckId }
            : { equipmentLabel: subject.equipmentLabel }),
          answers,
          notes: notes.trim() || undefined,
          latitude: location?.latitude,
          longitude: location?.longitude,
          photos: reportPhotoUrls.length
            ? photosFor(reportPhotoUrls)
            : undefined,
          signatureUrl,
          signedName: signedName.trim() || undefined,
          clientSubmissionId: submissionId.current,
        },
      });
      cleared.current = true;
      clearRun(templateId, submissionId.current);
      void removeForRun(submissionId.current);
      // Counted here rather than on the done screen, so a late outbox callback
      // cannot change the numbers on a report that is already filed.
      setFiled({
        id: (report as { id?: string } | null)?.id ?? '',
        passed: items.filter(isAnswered).length - failedItems.length,
        flagged: failedItems.length,
      });
      return true;
    } catch (error: unknown) {
      notify(messageFrom(error, 'Could not send the inspection'));
      return false;
    } finally {
      setSending(false);
    }
  };

  return {
    checklists,
    row,
    covers,
    kind,
    setSubjectKind,
    equipmentLabel,
    setEquipmentLabel,
    trucks,
    starting: start.isPending,
    data,
    template,
    items,
    stage,
    setStage,
    subject,
    startOn,
    unavailable,
    drafts,
    draftFor,
    viewFor,
    setDraft,
    isAnswered,
    pendingFor,
    pendingCount,
    reportPhotoUrls,
    reportPhotoCount,
    addFiles,
    removePhoto,
    removePending,
    notes,
    setNotes,
    signedName,
    setSignedName,
    signature,
    setSignature,
    online,
    required,
    doneCount,
    remaining,
    progress,
    failedItems,
    missingPhotos,
    outOfRange,
    statusLine,
    notSigned,
    firstBlock,
    hasWork,
    changeSubject,
    finish,
    sending,
    filed,
  };
}

export type InspectionRun = ReturnType<typeof useInspectionRun>;
